import type { MouseEvent, ReactNode } from "react";
import { Check, Loader2, Pencil, X } from "lucide-react";
import type { FieldState } from "@/domains/buildings/types/buildingWidget";
import type { BuildingDataWidgetSections } from "@/lib/i18n/types/buildings-sections/building-data-widget";

interface FieldControlsProps {
  state: FieldState;
  isSaving: boolean;
  hasConflict: boolean;
  expanded: boolean;
  valueOutsideEnum: boolean;
  tw: BuildingDataWidgetSections;
  onConfirm: () => void;
  onEdit: () => void;
  onClear: () => void;
  onExpand: () => void;
  onDismiss: () => void;
}

/** Circular control button used by accept, edit, reject, and dismiss actions. */
export function IconButton(props: {
  onClick: (event: MouseEvent) => void;
  title: string;
  color: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      title={props.title}
      style={{
        width: 22,
        height: 22,
        borderRadius: 6,
        border: "1px solid transparent",
        background: "transparent",
        color: props.color,
        display: "grid",
        placeItems: "center",
        cursor: "pointer",
        padding: 0,
      }}
    >
      {props.children}
    </button>
  );
}

function PendingFieldControls(props: FieldControlsProps) {
  if (!props.hasConflict) {
    return (
      <>
        <IconButton onClick={props.onConfirm} title={props.tw.confirmValue} color="var(--bd-ok)">
          <Check size={14} />
        </IconButton>
        <IconButton onClick={props.onEdit} title={props.tw.editValue} color="var(--bd-ink-2)">
          <Pencil size={13} />
        </IconButton>
        <IconButton onClick={props.onClear} title={props.tw.rejectValue} color="var(--bd-warn)">
          <X size={14} />
        </IconButton>
      </>
    );
  }
  if (!props.expanded) {
    return (
      <>
        <IconButton onClick={props.onExpand} title={props.tw.editValue} color="var(--bd-ink-2)">
          <Pencil size={13} />
        </IconButton>
        <IconButton onClick={props.onClear} title={props.tw.rejectValue} color="var(--bd-warn)">
          <X size={14} />
        </IconButton>
      </>
    );
  }
  return (
    <IconButton onClick={props.onDismiss} title={props.tw.dismiss} color="var(--bd-warn)">
      <X size={14} />
    </IconButton>
  );
}

/**
 * A saved value's tray: the pencil that starts an inline edit, plus the clear
 * action when the stored value is no longer one the dropdown can offer (a
 * pencil alone cannot repair that one).
 */
function ConfirmedFieldControls(props: FieldControlsProps) {
  return (
    <>
      <IconButton onClick={props.onEdit} title={props.tw.editValue} color="var(--bd-ink-2)">
        <Pencil size={13} />
      </IconButton>
      {props.valueOutsideEnum ? (
        <IconButton onClick={props.onClear} title={props.tw.clearValue} color="var(--bd-warn)">
          <X size={14} />
        </IconButton>
      ) : null}
    </>
  );
}

export function FieldControls(props: FieldControlsProps) {
  if (props.isSaving) {
    return <Loader2 size={10} className="animate-spin" style={{ color: "var(--bd-ink-4)" }} />;
  }
  if (props.state === "confirmed") return <ConfirmedFieldControls {...props} />;
  if (props.state === "pending") return <PendingFieldControls {...props} />;
  return null;
}
